import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';
import { SocialIcon, type SocialPlatform } from '../icons/SocialIcon';
import { SocialProfilePicture } from '../common/SocialProfilePicture';

interface DisconnectAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  accountName: string;
  username: string;
  pictureUrl?: string;
  platformName: string;
  platformId: SocialPlatform;
}

export const DisconnectAccountModal: React.FC<DisconnectAccountModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  accountName,
  username,
  pictureUrl,
  platformName,
  platformId
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const displayName = username || accountName;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
        
        <div className="relative bg-white rounded-xl border border-black shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              <h2 className="text-xl font-semibold flex items-center gap-2">
                {t('accounts.disconnectModal.title', 'Disconnect Account')}
                <SocialIcon platform={platformId} size={22} />
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <p className="text-gray-700">
              {t('accounts.disconnectModal.messagePart1', 
                `Are you sure you want to disconnect the ${platformName} account?`)}
            </p>

            <div className="flex items-center gap-2 my-3">
              <SocialProfilePicture 
                pictureUrl={pictureUrl}
                username={displayName} 
                platform={platformId} 
                size="lg"
              />
              <p className="text-gray-700 font-semibold">
                @{displayName}
              </p>
            </div>

            <p className="text-sm text-red-600">
              {t('accounts.disconnectModal.warning', 'This action cannot be undone. You will need to reconnect it to use its features.')}
            </p>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={() => {
                onConfirm();
                onClose(); // Close modal after confirmation
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
            >
              {t('common.confirm', 'Confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 